// src/hooks/useAuth.ts
import { useState, useEffect, useCallback } from 'react';

interface LoginCredentials {
  email: string;
  password: string;
  rememberMe: boolean;
}

interface RegisterFormData {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  confirmPassword?: string;
  agreeToTerms?: boolean;
}


interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  metadata?: any;
  phone?: string;
  dateOfBirth?: string;
  gender?: 'male' | 'female' | 'other' | '';
  address?: string;
  emergencyContact?: {
    name: string;
    phone: string;
    relationship: string;
  };
  healthGoals?: string[];
  medicalConditions?: string[];
  profilePicture?: string;
  createdAt: string;
  lastLogin?: string;
  heal_id?: string;
}

interface AuthState {
  isAuthenticated: boolean;
  user: User | null;
  token: string | null;
  isLoading: boolean;
}

interface UseAuthReturn {
  // State
  isAuthenticated: boolean;
  user: User | null;
  token: string | null;
  isLoading: boolean;

  // Actions
  login: (credentials: LoginCredentials) => Promise<void>;
  register: (data: RegisterFormData) => Promise<void>;
  logout: () => void;
  refreshToken: () => Promise<void>;
  refreshUser: () => Promise<void>;
  updateProfile: (profile: Partial<User>) => Promise<void>;

  // Utilities
  isTokenExpired: () => boolean;
  getAuthHeaders: () => Record<string, string>;
}

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:5001';

// Token management utilities
const TOKEN_KEY = 'access_token';
const USER_KEY = 'heal_user_data';

const getStoredToken = (): string | null => {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
};

const setStoredToken = (token: string): void => {
  try {
    localStorage.setItem(TOKEN_KEY, token);
  } catch (error) {
    console.warn('Failed to store auth token:', error);
  }
};

const removeStoredToken = (): void => {
  try {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
  } catch (error) {
    console.warn('Failed to remove auth token:', error);
  }
};

const getStoredUser = (): User | null => {
  try {
    const userData = localStorage.getItem(USER_KEY);
    return userData ? JSON.parse(userData) : null;
  } catch {
    return null;
  }
};

const setStoredUser = (user: User): void => {
  try {
    localStorage.setItem(USER_KEY, JSON.stringify(user));
  } catch (error) {
    console.warn('Failed to store user data:', error);
  }
};

const isTokenExpired = (token: string): boolean => {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.exp * 1000 < Date.now();
  } catch {
    return true;
  }
};

export const useAuth = (): UseAuthReturn => {
  const [authState, setAuthState] = useState<AuthState>({
    isAuthenticated: false,
    user: null,
    token: null,
    isLoading: true
  });

  // Initialize auth state from storage
  useEffect(() => {
    const initializeAuth = () => {
      const storedToken = getStoredToken();
      const storedUser = getStoredUser();

      if (storedToken && storedUser && !isTokenExpired(storedToken)) {
        setAuthState({
          isAuthenticated: true,
          user: storedUser,
          token: storedToken,
          isLoading: false
        });
      } else {
        // Clean up invalid/expired data
        removeStoredToken();
        setAuthState({
          isAuthenticated: false,
          user: null,
          token: null,
          isLoading: false
        });
      }
    };

    initializeAuth();
  }, []);

  const login = useCallback(async (credentials: LoginCredentials): Promise<void> => {
    try {
      setAuthState(prev => ({ ...prev, isLoading: true }));

      const response = await fetch(`${API_BASE_URL}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: credentials.email,
          password: credentials.password
        }),
      });

      // Parse response data
      const data = await response.json();

      if (!response.ok) {
        // Backend returned an error response
        const errorMessage = data.error?.message || data.message || 'Login failed';
        const errorCode = data.error?.code || 'LOGIN_FAILED';
        const errorType = data.error?.type || 'authentication_error';

        // Create a detailed error object
        const error: any = new Error(errorMessage);
        error.code = errorCode;
        error.type = errorType;
        error.status = response.status;
        error.error = data.error;

        throw error;
      }

      if (!data.success) {
        const errorMessage = data.error?.message || 'Login failed';
        const error: any = new Error(errorMessage);
        error.code = data.error?.code || 'LOGIN_FAILED';
        error.type = data.error?.type || 'authentication_error';
        throw error;
      }

      const { access_token, user } = data.data;  // ← Changed from 'token' to 'access_token'

      // Store token and user data
      setStoredToken(access_token);  // ← Changed
      setStoredUser(user);

      setAuthState({
        isAuthenticated: true,
        user,
        token: access_token,  // ← Changed
        isLoading: false
      });

    } catch (error: any) {
      // Only update isLoading, don't change isAuthenticated/user/token
      // This prevents unnecessary re-renders that can unmount components
      setAuthState(prev => ({
        ...prev,
        isLoading: false
      }));

      // Enhance network errors
      if (error.message?.includes('fetch') || error.name === 'TypeError') {
        const networkError: any = new Error('Unable to connect to server. Please check your internet connection.');
        networkError.code = 'NETWORK_ERROR';
        networkError.type = 'network_error';
        throw networkError;
      }

      throw error;
    }
  }, []);

  const register = useCallback(async (data: RegisterFormData): Promise<void> => {
    try {
      setAuthState(prev => ({ ...prev, isLoading: true }));

      const response = await fetch(`${API_BASE_URL}/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });

      const responseData = await response.json();

      if (!response.ok) {
        const errorMessage = responseData.error?.message || responseData.message || 'Registration failed';
        const errorCode = responseData.error?.code || 'REGISTRATION_FAILED';
        const errorType = responseData.error?.type || 'registration_error';

        const error: any = new Error(errorMessage);
        error.code = errorCode;
        error.type = errorType;
        error.status = response.status;
        error.error = responseData.error;

        throw error;
      }

      if (!responseData.success) {
        const errorMessage = responseData.error?.message || 'Registration failed';
        const error: any = new Error(errorMessage);
        error.code = responseData.error?.code || 'REGISTRATION_FAILED';
        error.type = responseData.error?.type || 'registration_error';
        throw error;
      }

      // IMPORTANT: Don't authenticate yet - user needs to verify email
      // Just clear the state and let App.tsx handle navigation to verification
      setAuthState({
        isAuthenticated: false,
        user: null,
        token: null,
        isLoading: false
      });

      // Don't store token/user yet - wait for email verification

    } catch (error: any) {
      setAuthState(prev => ({
        ...prev,
        isLoading: false,
        isAuthenticated: false,
        user: null,
        token: null
      }));

      // Enhance network errors
      if (error.message?.includes('fetch') || error.name === 'TypeError') {
        const networkError: any = new Error('Unable to connect to server. Please check your internet connection.');
        networkError.code = 'NETWORK_ERROR';
        networkError.type = 'network_error';
        throw networkError;
      }

      throw error;
    }
  }, []);

  const logout = useCallback(() => {
    removeStoredToken();
    setAuthState({
      isAuthenticated: false,
      user: null,
      token: null,
      isLoading: false
    });
  }, []);

  const refreshToken = useCallback(async (): Promise<void> => {
    try {
      const currentToken = getStoredToken();

      if (!currentToken) {
        throw new Error('No token to refresh');
      }

      const response = await fetch(`${API_BASE_URL}/auth/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${currentToken}`
        },
      });

      if (!response.ok) {
        throw new Error('Token refresh failed');
      }

      const data = await response.json();

      if (data.success && data.data.token) {
        setStoredToken(data.data.token);
        setAuthState(prev => ({
          ...prev,
          token: data.data.token
        }));
      }

    } catch (error) {
      console.error('Token refresh failed:', error);
      logout();
      throw error;
    }
  }, [logout]);

  const refreshUser = useCallback(async (): Promise<void> => {
    try {
      const currentToken = getStoredToken();

      if (!currentToken) {
        return;
      }

      const response = await fetch(`${API_BASE_URL}/auth/me`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${currentToken}`
        },
      });

      if (!response.ok) {
        // If auth/me fails (e.g. 401), we might want to logout or just ignore if it's network error
        // For now, let's just log it
        console.warn('Failed to refresh user data');
        return;
      }

      const data = await response.json();

      if (data.success && data.data) {
        // The backend returns user_info in data.data
        // We need to map it to our User interface if necessary, 
        // or ensure the backend returns the full user object structure.
        // Based on routes.md, /auth/me returns { user_id, email, metadata, role, heal_id }
        // Our local User interface has more fields. We should merge carefully or overwrite if backend sends full object.
        // Assuming /auth/me returns a compatible object or we merge it.

        // Let's retrieve the CURRENT stored user to merge with, to preserve fields not returned by /auth/me if any
        const currentUser = getStoredUser();

        const updatedUser = {
          ...currentUser,
          ...data.data,
          // Map backend fields to frontend User interface if needed
          id: data.data.user_id || currentUser?.id,
          email: data.data.email || currentUser?.email,
          heal_id: data.data.heal_id
        };

        setStoredUser(updatedUser);
        setAuthState(prev => ({
          ...prev,
          user: updatedUser
        }));
      }

    } catch (error) {
      console.error('User refresh failed:', error);
    }
  }, []);

  const updateProfile = useCallback(async (profile: Partial<User>): Promise<void> => {
    try {
      const currentToken = getStoredToken();

      if (!currentToken) {
        throw new Error('No authentication token');
      }

      const response = await fetch(`${API_BASE_URL}/auth/profile`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${currentToken}`
        },
        body: JSON.stringify(profile),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error?.message || 'Profile update failed');
      }

      const data = await response.json();

      if (data.success && data.data.user) {
        setStoredUser(data.data.user);
        setAuthState(prev => ({
          ...prev,
          user: data.data.user
        }));
      }

    } catch (error) {
      console.error('Profile update failed:', error);
      throw error;
    }
  }, []);

  const checkTokenExpired = useCallback((): boolean => {
    const token = getStoredToken();
    return token ? isTokenExpired(token) : true;
  }, []);

  const getAuthHeaders = useCallback((): Record<string, string> => {
    const token = getStoredToken();

    if (!token) {
      return {
        'Content-Type': 'application/json'
      };
    }

    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    };
  }, []);

  return {
    // State
    isAuthenticated: authState.isAuthenticated,
    user: authState.user,
    token: authState.token,
    isLoading: authState.isLoading,

    // Actions
    login,
    register,
    logout,
    refreshToken,
    refreshUser,
    updateProfile,

    // Utilities
    isTokenExpired: checkTokenExpired,
    getAuthHeaders
  };
};